import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  Plus,
  ClipboardList,
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  List,
  CheckSquare,
  Edit,
  Filter as FilterIcon,
} from "lucide-react";
import TaskList from "./TaskList";
import TaskFormModal from "./TaskFormModal";
import { useProjectTasks } from "../hooks/useProjectTasks";
import { getProjectTasks } from "../functions/api_fethcers";

/**
 * ProjectManagementSection - Component to display and manage projects related to a service
 *
 * @param {Object} selectedService - The currently selected service (null if none selected)
 * @param {Array} projects - List of projects for the selected service
 * @param {Function} onAddProject - Callback when "Add Project" button is clicked
 * @param {Function} onViewProject - Callback when a project is clicked to view details
 */
const ProjectManagementSection = ({
  selectedService,
  projects = [],
  isLoading = false,
  onAddProject,
  onViewProject,
  onDeleteProject,
}) => {
  // State for task management
  const [selectedProject, setSelectedProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [showTaskView, setShowTaskView] = useState(false);

  // State for project filtering
  const [statusFilter, setStatusFilter] = useState(null);

  // Create state to store task counts for all projects
  const [projectTaskCounts, setProjectTaskCounts] = useState({});

  // Use the project tasks hook for selected project
  const {
    tasks,
    taskStats,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useProjectTasks(selectedProject?._id, {});

  // Stable key derived from the actual set of project ids, so this effect
  // only re-runs (and re-fetches N project task lists) when the ids
  // themselves change, not on every re-render that hands us a new `projects`
  // array reference with the same underlying projects.
  const projectIdsKey = projects
    .map((project) => project._id)
    .filter(Boolean)
    .sort()
    .join(",");

  // Fetch task counts for all projects when the set of project ids changes
  useEffect(() => {
    const fetchTasksForAllProjects = async () => {
      const counts = {};

      for (const project of projects) {
        try {
          // If project ID exists, fetch its tasks
          if (project._id) {
            const tasksData = await getProjectTasks(project._id, {});
            counts[project._id] = tasksData.length || 0;
          }
        } catch (error) {
          console.error(
            `Error fetching tasks for project ${project._id}:`,
            error
          );
          counts[project._id] = 0;
        }
      }

      setProjectTaskCounts(counts);
    };

    if (projects.length > 0) {
      fetchTasksForAllProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdsKey]);

  // Handle adding a new task
  const handleAddTask = () => {
    setTaskToEdit(null);
    setShowTaskModal(true);
  };

  // Handle editing a task
  const handleEditTask = (task) => {
    setTaskToEdit(task);
    setShowTaskModal(true);
  };

  // Handle task form submission
  const handleTaskSubmit = async (taskData) => {
    try {
      if (taskData.taskId) {
        // Update existing task
        await updateTask({
          taskId: taskData.taskId,
          taskData: taskData.taskData,
        });
      } else {
        // Create new task
        await createTask(taskData.taskData);
      }

      // Update task counts for the current project
      if (selectedProject?._id) {
        const updatedTasks = await getProjectTasks(selectedProject._id, {});
        setProjectTaskCounts((prev) => ({
          ...prev,
          [selectedProject._id]: updatedTasks.length || 0,
        }));
      }
    } catch (error) {
      console.error("Error submitting task:", error);
      throw error;
    }
  };

  // Handle project click - shows tasks for the selected project
  const handleProjectClick = (project) => {
    if (selectedProject?._id === project._id) {
      // Toggle task view if clicking the same project
      setShowTaskView(!showTaskView);
    } else {
      // Select a new project and show its tasks - doesn't open edit form
      setSelectedProject(project);
      setShowTaskView(true);
      // Removed the onViewProject call here - now only happens on edit button click
    }
  };

  // Show placeholder if no service is selected
  if (!selectedService) {
    return (
      <div className="bg-white dark:bg-darkbg-800 rounded-lg p-8 shadow-md flex flex-col items-center justify-center min-h-[300px]">
        <ClipboardList className="w-16 h-16 text-grayteck-300 dark:text-grayteck-600 mb-4" />
        <p className="text-grayteck-500 dark:text-grayteck-400 text-lg">
          Select a service to view its projects
        </p>
        <p className="text-grayteck-400 dark:text-grayteck-500 text-sm mt-2 max-w-md text-center">
          Projects will be displayed here once you select a service from the
          list above
        </p>
      </div>
    );
  }

  // Function to get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "active":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      case "planning":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "on_hold":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
      default:
        return "bg-grayteck-100 text-grayteck-800 dark:bg-grayteck-900/30 dark:text-grayteck-300";
    }
  };

  // Function to get status icon
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return (
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
        );
      case "active":
        return <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case "cancelled":
        return (
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        );
      case "planning":
        return (
          <ClipboardList className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
        );
      case "on_hold":
        return (
          <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        );
      default:
        return (
          <ClipboardList className="w-5 h-5 text-grayteck-600 dark:text-grayteck-400" />
        );
    }
  };

  // Helper function to calculate project progress
  const calculateProgress = (project) => {
    // If you track tasks or time entries, you could implement a proper calculation here
    // For now, we'll return a fixed progress or 100% if project is completed
    if (project.status === "completed") return 100;

    // If there are tasks, calculate progress based on completed tasks
    if (project.tasks && project.tasks.length > 0) {
      const completedTasks = project.tasks.filter(
        (task) => task.status === "completed"
      ).length;
      return Math.round((completedTasks / project.tasks.length) * 100);
    }

    // Default progress values by status
    const progressByStatus = {
      planning: 10,
      active: 50,
      on_hold: 60,
      cancelled: 0,
    };

    return progressByStatus[project.status] || 0;
  };

  // Calculate project counts by status
  const completedProjects = projects.filter(
    (p) => p.status?.toLowerCase() === "completed"
  ).length;
  const activeProjects = projects.filter(
    (p) => p.status?.toLowerCase() === "active"
  ).length;
  const planningProjects = projects.filter(
    (p) => p.status?.toLowerCase() === "planning"
  ).length;
  const onHoldProjects = projects.filter(
    (p) => p.status?.toLowerCase() === "on_hold"
  ).length;
  const cancelledProjects = projects.filter(
    (p) => p.status?.toLowerCase() === "cancelled"
  ).length;

  return (
    <div className="space-y-6">
      {/* Header with add project button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
            Projects for {selectedService.service_code}
          </h3>
          <p className="text-sm text-grayteck-600 dark:text-grayteck-400 mt-1">
            {projects.length} total projects
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onAddProject && onAddProject(selectedService)}
          className="px-4 py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 
                   flex items-center space-x-2 transition-colors duration-200"
        >
          <Plus className="w-5 h-5" />
          <span>Add Project</span>
        </motion.button>
      </div>

      {/* Project stats with filter functionality */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* "All" status card */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setStatusFilter(null)}
          className={`bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md cursor-pointer transition-all
                    ${
                      statusFilter === null
                        ? "ring-2 ring-grayteck-500 dark:ring-grayteck-400"
                        : "hover:shadow-lg"
                    }`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-grayteck-100 dark:bg-grayteck-900/30 rounded-lg">
              <ClipboardList className="w-5 h-5 text-grayteck-600 dark:text-grayteck-400" />
            </div>
            <div>
              <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
                All Projects
              </p>
              <p className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
                {projects.length}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Planning */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setStatusFilter("planning")}
          className={`bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md cursor-pointer transition-all
                    ${
                      statusFilter === "planning"
                        ? "ring-2 ring-yellow-500 dark:ring-yellow-400"
                        : "hover:shadow-lg"
                    }`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <ClipboardList className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
                Planning
              </p>
              <p className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
                {planningProjects}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Active */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setStatusFilter("active")}
          className={`bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md cursor-pointer transition-all
                    ${
                      statusFilter === "active"
                        ? "ring-2 ring-blue-500 dark:ring-blue-400"
                        : "hover:shadow-lg"
                    }`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
                Active
              </p>
              <p className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
                {activeProjects}
              </p>
            </div>
          </div>
        </motion.div>

        {/* On Hold */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setStatusFilter("on_hold")}
          className={`bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md cursor-pointer transition-all
                    ${
                      statusFilter === "on_hold"
                        ? "ring-2 ring-purple-500 dark:ring-purple-400"
                        : "hover:shadow-lg"
                    }`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
                On Hold
              </p>
              <p className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
                {onHoldProjects}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Completed */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setStatusFilter("completed")}
          className={`bg-white dark:bg-darkbg-800 rounded-lg p-4 shadow-md cursor-pointer transition-all
                    ${
                      statusFilter === "completed"
                        ? "ring-2 ring-green-500 dark:ring-green-400"
                        : "hover:shadow-lg"
                    }`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
                Completed
              </p>
              <p className="text-xl font-semibold text-grayteck-900 dark:text-grayteck-100">
                {completedProjects}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filter indicator - shows when a filter is active */}
      {statusFilter && (
        <div className="mt-4 flex items-center justify-between bg-grayteck-50 dark:bg-darkbg-900 p-3 rounded-lg border border-grayteck-200 dark:border-grayteck-700">
          <div className="flex items-center">
            <FilterIcon className="w-4 h-4 text-grayteck-600 dark:text-grayteck-400 mr-2" />
            <span className="text-sm text-grayteck-700 dark:text-grayteck-300">
              Filtered by status:{" "}
              <span className="font-medium">
                {statusFilter.charAt(0).toUpperCase() +
                  statusFilter.slice(1).replace("_", " ")}
              </span>
            </span>
          </div>
          <button
            onClick={() => setStatusFilter(null)}
            className="text-xs px-2 py-1 bg-grayteck-200 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 rounded hover:bg-grayteck-300 dark:hover:bg-darkbg-600 transition-colors"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Project list with filtering */}
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-grayteck-500 border-t-transparent"></div>
        </div>
      ) : projects.length > 0 &&
        projects.filter((project) =>
          statusFilter ? project.status === statusFilter : true
        ).length > 0 ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4">
            {/* Filter projects by status if filter is active */}
            {projects
              .filter((project) =>
                statusFilter ? project.status === statusFilter : true
              )
              .map((project, index) => (
                <div key={project.id || index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white dark:bg-darkbg-800 rounded-lg p-6 shadow-md 
                           hover:shadow-lg transition-shadow cursor-pointer relative
                           ${
                             selectedProject?._id === project._id
                               ? "ring-2 ring-grayteck-600 dark:ring-grayteck-400"
                               : ""
                           }`}
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="flex justify-between">
                      <div className="mr-16">
                        {" "}
                        {/* Added margin to make space for the buttons */}
                        <h4 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
                          {project.name}
                        </h4>
                        <div className="mt-2 flex items-center">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                              project.status
                            )}`}
                          >
                            {getStatusIcon(project.status)}
                            <span className="ml-1">{project.status}</span>
                          </span>
                        </div>
                      </div>

                      <div className="text-right mt-8">
                        {" "}
                        {/* Added margin-top to move text down */}
                        <p className="text-sm text-grayteck-600 dark:text-grayteck-400 truncate max-w-[140px]">
                          Due:{" "}
                          {project.target_completion_date
                            ? new Date(
                                project.target_completion_date
                              ).toLocaleDateString()
                            : "N/A"}
                        </p>
                        <p className="text-sm text-grayteck-600 dark:text-grayteck-400 mt-1 truncate max-w-[140px]">
                          Budget:{" "}
                          {project.budget_hours
                            ? `${project.budget_hours} hours`
                            : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-4 h-2 bg-grayteck-200 dark:bg-grayteck-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          project.status?.toLowerCase() === "completed"
                            ? "bg-green-500"
                            : project.status?.toLowerCase() === "cancelled"
                            ? "bg-red-500"
                            : project.status?.toLowerCase() === "active"
                            ? "bg-blue-500"
                            : project.status?.toLowerCase() === "on_hold"
                            ? "bg-purple-500"
                            : "bg-yellow-500"
                        }`}
                        style={{ width: `${calculateProgress(project)}%` }}
                      />
                    </div>

                    {/* Description - truncated */}
                    {project.description && (
                      <p className="mt-4 text-sm text-grayteck-600 dark:text-grayteck-400 line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Add Task button */}
                    <div className="mt-4 pt-3 border-t border-grayteck-200 dark:border-grayteck-700 flex justify-between">
                      <div className="flex items-center space-x-1">
                        <CheckSquare className="w-4 h-4 text-grayteck-600 dark:text-grayteck-400" />
                        <span className="text-sm text-grayteck-600 dark:text-grayteck-400">
                          {selectedProject?._id === project._id
                            ? taskStats.total || 0
                            : projectTaskCounts[project._id] || 0}{" "}
                          Tasks
                        </span>
                      </div>
                      {selectedProject?._id === project._id && (
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddTask();
                          }}
                          className="px-2 py-1 text-xs bg-grayteck-500 text-white rounded hover:bg-grayteck-600 
                                 flex items-center space-x-1 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Add Task</span>
                        </motion.button>
                      )}
                    </div>

                    {/* Project action buttons - moved away from the text */}
                    <div className="absolute top-3 right-3 flex space-x-1 z-10 shadow-sm">
                      {/* Edit project button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewProject && onViewProject(project); // This calls the edit project function
                        }}
                        className="p-1.5 text-grayteck-600 hover:text-grayteck-800 dark:text-grayteck-400 
                               dark:hover:text-grayteck-200 bg-white dark:bg-darkbg-800 rounded-md
                               hover:bg-grayteck-100 dark:hover:bg-grayteck-900/30 border border-grayteck-200 dark:border-grayteck-700"
                        title="Edit Project"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete project button */}
                      {onDeleteProject && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteProject(project._id);
                          }}
                          className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 
                                 dark:hover:text-red-300 bg-white dark:bg-darkbg-800 rounded-md
                                 hover:bg-red-100 dark:hover:bg-red-900/30 border border-grayteck-200 dark:border-grayteck-700"
                          title="Delete Project"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </motion.div>

                  {/* Task List */}
                  {selectedProject?._id === project._id && showTaskView && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      transition={{ duration: 0.3 }}
                      className="mt-2 bg-gray-50 dark:bg-darkbg-900 rounded-lg p-4 border border-grayteck-200 dark:border-grayteck-700"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center space-x-2">
                          <List className="h-5 w-5 text-grayteck-600 dark:text-grayteck-400" />
                          <h3 className="text-md font-medium text-grayteck-800 dark:text-grayteck-200">
                            Tasks
                          </h3>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleAddTask}
                          className="px-3 py-1 bg-grayteck-600 text-white text-sm rounded-md hover:bg-grayteck-700 
                                 flex items-center space-x-1 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>New Task</span>
                        </motion.button>
                      </div>

                      <TaskList
                        tasks={tasks}
                        isLoading={isLoadingTasks}
                        onUpdateTaskStatus={async (data) => {
                          await updateTaskStatus(data);
                          // Update task counts after status change
                          if (selectedProject?._id) {
                            const updatedTasks = await getProjectTasks(
                              selectedProject._id,
                              {}
                            );
                            setProjectTaskCounts((prev) => ({
                              ...prev,
                              [selectedProject._id]: updatedTasks.length || 0,
                            }));
                          }
                        }}
                        onEditTask={handleEditTask}
                        onDeleteTask={async (taskId) => {
                          await deleteTask(taskId);
                          // Update task counts after deletion
                          if (selectedProject?._id) {
                            const updatedTasks = await getProjectTasks(
                              selectedProject._id,
                              {}
                            );
                            setProjectTaskCounts((prev) => ({
                              ...prev,
                              [selectedProject._id]: updatedTasks.length || 0,
                            }));
                          }
                        }}
                        showFilters={tasks.length > 3} // Only show filters if there are enough tasks
                      />
                    </motion.div>
                  )}
                </div>
              ))}
          </div>

          {/* Task Form Modal */}
          <TaskFormModal
            isOpen={showTaskModal}
            onClose={() => setShowTaskModal(false)}
            onSubmit={handleTaskSubmit}
            projectId={selectedProject?._id}
            taskToEdit={taskToEdit}
          />
        </div>
      ) : statusFilter && projects.length > 0 ? (
        // Show this when there are projects but none match the current filter
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-8 shadow-md flex flex-col items-center justify-center min-h-[200px]">
          <FilterIcon className="w-12 h-12 text-grayteck-300 dark:text-grayteck-600 mb-3" />
          <p className="text-grayteck-500 dark:text-grayteck-400 text-lg">
            No {statusFilter.replace("_", " ")} projects found
          </p>
          <p className="text-grayteck-400 dark:text-grayteck-500 text-sm mt-2 max-w-md text-center">
            There are no projects with "{statusFilter.replace("_", " ")}" status
          </p>
          <button
            onClick={() => setStatusFilter(null)}
            className="mt-4 px-4 py-2 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 
                   rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 transition-colors"
          >
            Show All Projects
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-darkbg-800 rounded-lg p-8 shadow-md flex flex-col items-center justify-center min-h-[200px]">
          <ClipboardList className="w-12 h-12 text-grayteck-300 dark:text-grayteck-600 mb-3" />
          <p className="text-grayteck-500 dark:text-grayteck-400 text-lg">
            {statusFilter
              ? `No ${statusFilter.replace("_", " ")} projects found`
              : "No projects found"}
          </p>
          <p className="text-grayteck-400 dark:text-grayteck-500 text-sm mt-2 max-w-md text-center">
            {statusFilter
              ? `Try selecting a different status filter or create a new project`
              : `Create a new project by clicking the "Add Project" button`}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectManagementSection;
